//
// Copyright (c) 2011-2022 by Curalate, Inc.
//
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { getTikTokUser } from '@apis/tiktok-user/tiktokUserApi';
import { ITikTokUser } from '@apis/tiktok-user/tiktokUserApiModel';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getTikTokUserIsFetchingUser } from '@tiktok/redux/user/selectors/getTikTokUser';
import { ITikTokUserState } from '@tiktok/redux/user/tiktokUserModel';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

const initialState: ITikTokUserState = {
  user: undefined,
  flags: {
    isFetchingTikTokUser: false,
  },
  errors: {
    fetchTikTokUserError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.TikTokUser,
  initialState,
  reducers: {
    requestTikTokUser(state) {
      state.flags.isFetchingTikTokUser = true;
      state.errors.fetchTikTokUserError = undefined;
    },
    receiveTikTokUserSucceeded(
      state,
      action: PayloadAction<ITikTokUser | undefined>,
    ) {
      state.flags.isFetchingTikTokUser = false;
      state.user = action.payload;
    },
    receiveTikTokUserFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingTikTokUser = false;
      state.errors.fetchTikTokUserError = action.payload;
    },
  },
});

export const fetchUser = createThunk(() => async (dispatch, getState) => {
  const isFetchingTikTokUser = getTikTokUserIsFetchingUser(getState());

  // If we are currently already attempting to fetch the current user, do not try to fetch again
  if (isFetchingTikTokUser) {
    return;
  }

  dispatch(actions.requestTikTokUser());

  try {
    const userResponse = await getTikTokUser();

    dispatch(actions.receiveTikTokUserSucceeded(userResponse));
  } catch (e) {
    dispatch(
      actions.receiveTikTokUserFailed(
        getRequestErrorMessage(e) ??
          'An unknown error occurred when attempting to fetch TikTok user',
      ),
    );
  }
});

export default reducer;
